/**
 * Client-side favourites for the public method library (task 2.4).
 *
 * localStorage, not D1, on purpose: /kit is an ANONYMOUS surface — the whole point of the
 * `/public/*` routes is that a visitor needs no session — so a server-side favourite would force a
 * login on the one screen designed not to need one. Same posture as `agendaStore`. When accounts
 * reach the Kit surface, this becomes the local half of a sync, not something to throw away.
 *
 * Keyed by SLUG, never by `Method.id`. Ids are `m-<locale>-<slug>`, so keying by id would silently
 * un-favourite every method the moment the visitor switched language.
 */
const KEY = "klapzo.favourites.v1";

export function loadFavourites(): Set<string> {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === "string") : []);
  } catch {
    return new Set();
  }
}

function save(slugs: Set<string>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify([...slugs]));
  } catch {
    /* private mode / quota — favourites are a convenience, never block the page */
  }
}

/** Returns the new set so callers can drive React state from the result. */
export function toggleFavourite(slug: string): Set<string> {
  const next = loadFavourites();
  if (!next.delete(slug)) next.add(slug);
  save(next);
  return next;
}
